import "@/App.css";
import { BrowserRouter, Route, Routes, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import ServerDetail from "@/pages/ServerDetail";
import SubmitServer from "@/pages/SubmitServer";
import Dashboard from "@/pages/Dashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import CategoryPage from "@/pages/CategoryPage";
import FeaturedPage from "@/pages/FeaturedPage";
import Admin from "@/pages/Admin";
import AdminReports from "@/pages/AdminReports";
import AdminStats from "@/pages/AdminStats";
import AdminModeration from "@/pages/AdminModeration";
import AdminUsers from "@/pages/AdminUsers";
import Pricing from "@/pages/Pricing";
import ApplyIndex from "@/pages/ApplyIndex";
import ApplyForm from "@/pages/ApplyForm";
import OwnerPositions from "@/pages/OwnerPositions";
import OwnerApplications from "@/pages/OwnerApplications";
import DiscordCallback from "@/pages/DiscordCallback";
import BotCallback from "@/pages/BotCallback";
import BestList from "@/pages/BestList";
import VsDisboard from "@/pages/VsDisboard";
import Status from "@/pages/Status";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import { usePresenceHeartbeat } from "@/lib/presence";

// Legacy redirect: /server/:id → /servers/:slug (canonical)
function LegacyServerRedirect() {
  const { id } = useParams();
  const [target, setTarget] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/servers/${id}`);
        setTarget(`/servers/${data.slug || data.id}`);
      } catch {
        setTarget("/servers");
      }
    })();
  }, [id]);
  if (!target) return null;
  return <Navigate to={target} replace />;
}

function AppRouter() {
  usePresenceHeartbeat();
  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/servers" element={<Browse />} />
          <Route path="/servers/featured" element={<FeaturedPage />} />
          <Route path="/servers/category/:categoryId" element={<CategoryPage />} />
          <Route path="/servers/:slug" element={<ServerDetail />} />
          {/* Legacy routes */}
          <Route path="/browse" element={<Navigate to="/servers" replace />} />
          <Route path="/server/:id" element={<LegacyServerRedirect />} />
          <Route path="/submit" element={<SubmitServer />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/stats" element={<AdminStats />} />
          <Route path="/admin/moderation" element={<AdminModeration />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/apply" element={<ApplyIndex />} />
          <Route path="/apply/:positionId" element={<ApplyForm />} />
          <Route path="/owner/positions" element={<OwnerPositions />} />
          <Route path="/owner/applications" element={<OwnerApplications />} />
          <Route path="/auth/discord/callback" element={<DiscordCallback />} />
          <Route path="/servers/bot/callback" element={<BotCallback />} />
          <Route path="/best/:slug" element={<BestList />} />
          <Route path="/serverlistr-vs-disboard" element={<VsDisboard />} />
          <Route path="/status" element={<Status />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App grain">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#0A0A0F",
                  border: "1px solid rgba(255,255,255,0.08)",
                  color: "#fff",
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
